import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { Calendar, Popover } from 'antd'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import styles from './DateRangePicker.module.css'

dayjs.extend(customParseFormat)

const DATE_PARSE_FORMATS = [
    'DD/MM/YY',
    'D/M/YY',
    'DD-MM-YY',
    'D-M-YY',
    'DD/MM/YYYY',
    'D/M/YYYY',
    'DD-MM-YYYY',
    'D-M-YYYY',
]

const DEFAULT_INPUT_DATE_FORMAT = 'DD/MM/YY'
const POPOVER_WIDTH_ESTIMATE = 280
const POPOVER_HEIGHT_ESTIMATE = 420
const VIEWPORT_EDGE_GAP = 8
const PLACEMENT_GAP = 4

/**
 * Pick the placement where the popover fits best in the viewport
 * (top / bottom / left / right + corners), preferring below-left like Asana.
 */
const resolveBestPlacement = (triggerEl) => {
    if (!triggerEl || typeof window === 'undefined') return 'bottomLeft'

    const rect = triggerEl.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const w = Math.min(POPOVER_WIDTH_ESTIMATE, vw - VIEWPORT_EDGE_GAP * 2)
    const h = Math.min(POPOVER_HEIGHT_ESTIMATE, vh - VIEWPORT_EDGE_GAP * 2)

    const candidates = [
        'bottomLeft',
        'bottomRight',
        'bottom',
        'topLeft',
        'topRight',
        'top',
        'rightTop',
        'rightBottom',
        'right',
        'leftTop',
        'leftBottom',
        'left',
    ]

    const getOrigin = (placement) => {
        switch (placement) {
            case 'bottomLeft':
                return { left: rect.left, top: rect.bottom + PLACEMENT_GAP }
            case 'bottomRight':
                return {
                    left: rect.right - w,
                    top: rect.bottom + PLACEMENT_GAP,
                }
            case 'bottom':
                return {
                    left: rect.left + rect.width / 2 - w / 2,
                    top: rect.bottom + PLACEMENT_GAP,
                }
            case 'topLeft':
                return { left: rect.left, top: rect.top - h - PLACEMENT_GAP }
            case 'topRight':
                return {
                    left: rect.right - w,
                    top: rect.top - h - PLACEMENT_GAP,
                }
            case 'top':
                return {
                    left: rect.left + rect.width / 2 - w / 2,
                    top: rect.top - h - PLACEMENT_GAP,
                }
            case 'rightTop':
                return { left: rect.right + PLACEMENT_GAP, top: rect.top }
            case 'rightBottom':
                return {
                    left: rect.right + PLACEMENT_GAP,
                    top: rect.bottom - h,
                }
            case 'right':
                return {
                    left: rect.right + PLACEMENT_GAP,
                    top: rect.top + rect.height / 2 - h / 2,
                }
            case 'leftTop':
                return { left: rect.left - w - PLACEMENT_GAP, top: rect.top }
            case 'leftBottom':
                return {
                    left: rect.left - w - PLACEMENT_GAP,
                    top: rect.bottom - h,
                }
            case 'left':
                return {
                    left: rect.left - w - PLACEMENT_GAP,
                    top: rect.top + rect.height / 2 - h / 2,
                }
            default:
                return { left: rect.left, top: rect.bottom + PLACEMENT_GAP }
        }
    }

    const scorePlacement = (placement) => {
        const { left, top } = getOrigin(placement)
        const overflowX =
            Math.max(0, VIEWPORT_EDGE_GAP - left) +
            Math.max(0, left + w - (vw - VIEWPORT_EDGE_GAP))
        const overflowY =
            Math.max(0, VIEWPORT_EDGE_GAP - top) +
            Math.max(0, top + h - (vh - VIEWPORT_EDGE_GAP))
        return overflowX + overflowY
    }

    let best = 'bottomLeft'
    let bestScore = Number.POSITIVE_INFINITY
    for (const placement of candidates) {
        const score = scorePlacement(placement)
        if (score < bestScore) {
            bestScore = score
            best = placement
            if (score === 0) break
        }
    }
    return best
}

const parseManualDate = (text) => {
    const trimmed = String(text ?? '').trim()
    if (!trimmed) return null

    for (const fmt of DATE_PARSE_FORMATS) {
        const parsed = dayjs(trimmed, fmt, true)
        if (parsed.isValid()) return parsed.startOf('day')
    }

    const loose = dayjs(trimmed)
    return loose.isValid() ? loose.startOf('day') : null
}

const normalizeRange = (start, end, editedField) => {
    let nextStart = start
    let nextEnd = end

    if (nextStart && nextEnd && nextStart.isAfter(nextEnd, 'day')) {
        if (editedField === 'start') {
            nextEnd = nextStart
        } else {
            nextStart = nextEnd
        }
    }

    return {
        start: nextStart?.isValid() ? nextStart.valueOf() : null,
        end: nextEnd?.isValid() ? nextEnd.valueOf() : null,
    }
}

const DateRangePicker = ({
    value,
    onChange,
    placeholder = 'No due date',
    parentComponent,
    getPopupContainer,
    dateFormat,
    showClear = true,
    disabled = false,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
}) => {
    const isDisabled = disabled || typeof onChange !== 'function'
    const isDetailDrawerPicker =
        parentComponent === 'taskDetailView' ||
        parentComponent === 'storyMapReleaseDetail'

    const inputDateFormat = dateFormat || DEFAULT_INPUT_DATE_FORMAT

    const formatInputDate = useCallback(
        (d) => (d?.isValid() ? d.format(inputDateFormat) : ''),
        [inputDateFormat]
    )
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const isControlled = controlledOpen !== undefined
    const open = isControlled ? Boolean(controlledOpen) : uncontrolledOpen
    const setOpen = useCallback(
        (nextOpen) => {
            if (!isControlled) setUncontrolledOpen(nextOpen)
            controlledOnOpenChange?.(nextOpen)
        },
        [isControlled, controlledOnOpenChange]
    )
    const [placement, setPlacement] = useState('bottomLeft')
    const [activeTab, setActiveTab] = useState('start') // 'start' | 'due'
    const [startInputText, setStartInputText] = useState('')
    const [dueInputText, setDueInputText] = useState('')
    // Tracks the month/year the user has navigated to in the calendar panel.
    // This is purely a VIEW state — it does not affect the committed start/end value.
    const [calendarViewDate, setCalendarViewDate] = useState(null)
    const panelRef = useRef(null)
    const triggerRef = useRef(null)
    const activeTabRef = useRef(activeTab)
    const dateSelectedRef = useRef(false)
    const suppressBlurCommitRef = useRef(false)
    const calendarInteractionRef = useRef(false)

    activeTabRef.current = activeTab

    const handleOpenChange = useCallback(
        (nextOpen) => {
            if (isDisabled) {
                setOpen(false)
                return
            }
            if (nextOpen) {
                setPlacement(resolveBestPlacement(triggerRef.current))
            }
            setOpen(nextOpen)
        },
        [isDisabled, setOpen]
    )

    const armCalendarInteraction = useCallback(() => {
        calendarInteractionRef.current = true
        window.setTimeout(() => {
            calendarInteractionRef.current = false
        }, 500)
    }, [])

    const scheduleBlurCommitResume = useCallback(() => {
        suppressBlurCommitRef.current = true
        window.setTimeout(() => {
            suppressBlurCommitRef.current = false
        }, 500)
    }, [])

    const startDate = useMemo(() => {
        if (!value?.start) return null
        const d = dayjs(value.start)
        return d.isValid() ? d.startOf('day') : null
    }, [value?.start])

    const endDate = useMemo(() => {
        if (!value?.end) return null
        const d = dayjs(value.end)
        return d.isValid() ? d.startOf('day') : null
    }, [value?.end])

    const syncInputsFromValue = useCallback(() => {
        setStartInputText(formatInputDate(startDate))
        setDueInputText(formatInputDate(endDate))
    }, [startDate, endDate])

    useEffect(() => {
        if (open) {
            syncInputsFromValue()
            setActiveTab('start') // always open on the start-date tab
            setCalendarViewDate(null) // reset so calendar starts at the committed date's month
            setPlacement(resolveBestPlacement(triggerRef.current))
        }
    }, [open, syncInputsFromValue])

    const calendarPanelValue = useMemo(() => {
        // If the user has navigated the calendar, show that navigated month/year.
        // calendarViewDate is never committed — it is only a display position.
        if (calendarViewDate) return calendarViewDate

        // Otherwise show the committed date for the active tab.
        if (activeTab === 'start') {
            if (startDate) return startDate
            if (endDate) return endDate
            return dayjs()
        }
        if (endDate) return endDate
        if (startDate) return startDate
        return dayjs()
    }, [calendarViewDate, activeTab, startDate, endDate])

    const commitRange = useCallback(
        (nextStart, nextEnd, editedField, options = {}) => {
            const { switchToDue = false } = options
            const normalized = normalizeRange(nextStart, nextEnd, editedField)
            onChange?.(normalized)

            const committedStart = normalized.start
                ? dayjs(normalized.start).startOf('day')
                : null
            const committedEnd = normalized.end
                ? dayjs(normalized.end).startOf('day')
                : null

            setStartInputText(formatInputDate(committedStart))
            setDueInputText(formatInputDate(committedEnd))

            if (switchToDue) {
                setActiveTab('due')
                setCalendarViewDate(null) // clear navigation so due tab shows its own committed date
                scheduleBlurCommitResume()
            }
        },
        [onChange, formatInputDate, scheduleBlurCommitResume]
    )

    const handleDateChange = (date) => {
        if (!date) return
        if (typeof date.isValid === 'function' && !date.isValid()) return
        const d = date.startOf('day')

        // ALWAYS update the calendar view state so the panel reflects the
        // navigated month/year. This is what was missing in the previous fix —
        // without this, the controlled `value` prop never changed and the Calendar
        // snapped back to the committed date's month on every render.
        setCalendarViewDate(d)

        // Only commit the actual start/end value when the user explicitly clicked
        // a date cell (mousedown sets the flag) or pressed Enter on a focused cell.
        // Ant Design v5 Calendar fires onChange for navigation too — the flag
        // distinguishes the two cases.
        if (!dateSelectedRef.current) return
        dateSelectedRef.current = false

        const tab = activeTabRef.current
        if (tab === 'start') {
            commitRange(d, endDate, 'start', { switchToDue: true })
        } else {
            commitRange(startDate, d, 'due')
        }
    }

    const handleCalendarKeyDown = (e) => {
        if (e.key === 'Enter') {
            dateSelectedRef.current = true
        }
    }

    // onPanelChange fires when the user clicks the month/year header to enter
    // month-picker or year-picker mode, and again when they confirm a selection.
    // It is the authoritative signal for panel navigation (no date committed here).
    const handlePanelChange = (date) => {
        if (date && typeof date.isValid === 'function' && date.isValid()) {
            setCalendarViewDate(date.startOf('day'))
        }
    }

    const shouldSkipBlurCommit = () =>
        suppressBlurCommitRef.current || calendarInteractionRef.current

    const commitInputBlur = (field) => {
        if (shouldSkipBlurCommit()) return

        const text = field === 'start' ? startInputText : dueInputText
        const trimmed = text.trim()

        if (!trimmed) {
            if (field === 'start') {
                if (startDate) {
                    setStartInputText(formatInputDate(startDate))
                    return
                }
                commitRange(null, endDate, 'start')
            } else {
                // Due field is often empty while picking an end date from the calendar.
                // Sync from props instead of emitting end: null on blur.
                setDueInputText(formatInputDate(endDate))
            }
            return
        }

        const parsed = parseManualDate(trimmed)
        if (!parsed) {
            if (field === 'start') {
                setStartInputText(formatInputDate(startDate))
            } else {
                setDueInputText(formatInputDate(endDate))
            }
            return
        }

        const sameAsCurrent =
            field === 'start'
                ? startDate?.isSame(parsed, 'day')
                : endDate?.isSame(parsed, 'day')

        if (field === 'start') {
            if (!sameAsCurrent) {
                commitRange(parsed, endDate, 'start')
            } else {
                setStartInputText(formatInputDate(startDate))
            }
        } else if (!sameAsCurrent) {
            commitRange(startDate, parsed, 'due')
        } else {
            setDueInputText(formatInputDate(endDate))
        }
    }

    /** Defer blur so calendar clicks / tab switch don't commit with wrong active tab */
    const handleInputBlur = (field) => {
        window.setTimeout(() => {
            if (shouldSkipBlurCommit()) return
            if (panelRef.current?.contains(document.activeElement)) {
                return
            }
            commitInputBlur(field)
        }, 0)
    }

    const handleCalendarMouseDown = (e) => {
        if (e.target.closest('.ant-picker-cell')) {
            e.preventDefault()
            dateSelectedRef.current = true
            armCalendarInteraction()
        }
    }

    const disabledDate = (current) => {
        if (!current) return false
        if (activeTab === 'start' && endDate) {
            return current.isAfter(endDate, 'day')
        }
        if (activeTab === 'due' && startDate) {
            return current.isBefore(startDate, 'day')
        }
        return false
    }

    const handleClear = (e) => {
        e.stopPropagation()
        onChange?.({ start: null, end: null })
        setStartInputText('')
        setDueInputText('')
        setOpen(false)
    }

    const formatDateRange = () => {
        if (dateFormat) {
            if (startDate && endDate) {
                return `${startDate.format(dateFormat)} – ${endDate.format(dateFormat)}`
            }
            if (endDate) return endDate.format(dateFormat)
            if (startDate) return startDate.format(dateFormat)
            return null
        }

        const currentYear = dayjs().year()

        const formatDate = (d) =>
            d.year() === currentYear
                ? d.format('D MMM')
                : d.format('D MMM, YYYY')

        if (startDate && endDate) {
            if (startDate.isSame(endDate, 'month')) {
                // Same month + year: "29 – 30 Sep" or "29 – 30 Sep, 2024"
                const monthSuffix =
                    startDate.year() === currentYear
                        ? startDate.format('MMM')
                        : startDate.format('MMM, YYYY')
                return `${startDate.format('D')} – ${endDate.format('D')} ${monthSuffix}`
            }
            // Different month or year: "29 Aug – 3 Sep" or "29 Aug, 2024 – 3 Sep, 2024"
            return `${formatDate(startDate)} – ${formatDate(endDate)}`
        }
        if (endDate) return formatDate(endDate)
        if (startDate) return formatDate(startDate)
        return null
    }

    const dateRangeText = formatDateRange()

    const content = (
        <div ref={panelRef} className={styles.datePickerContainer}>
            <div className={styles.tabs} role="tablist" aria-label="Date range">
                <div
                    className={`${styles.tabField} ${
                        activeTab === 'start' ? styles.activeTab : ''
                    }`}
                >
                    <input
                        type="text"
                        role="tab"
                        aria-selected={activeTab === 'start'}
                        className={styles.tabInput}
                        placeholder="Start date"
                        value={startInputText}
                        onFocus={() => {
                            setActiveTab('start')
                            setCalendarViewDate(null)
                        }}
                        onChange={(e) => setStartInputText(e.target.value)}
                        onBlur={() => handleInputBlur('start')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur()
                        }}
                    />
                </div>
                <div
                    className={`${styles.tabField} ${
                        activeTab === 'due' ? styles.activeTab : ''
                    }`}
                >
                    <input
                        type="text"
                        role="tab"
                        aria-selected={activeTab === 'due'}
                        className={styles.tabInput}
                        placeholder="Due date"
                        value={dueInputText}
                        onFocus={() => {
                            setActiveTab('due')
                            setCalendarViewDate(null)
                        }}
                        onChange={(e) => setDueInputText(e.target.value)}
                        onBlur={() => handleInputBlur('due')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur()
                        }}
                    />
                </div>
            </div>
            <div
                className={styles.calendarWrapper}
                onMouseDownCapture={handleCalendarMouseDown}
                onKeyDown={handleCalendarKeyDown}
            >
                <Calendar
                    value={calendarPanelValue}
                    onChange={handleDateChange}
                    onPanelChange={handlePanelChange}
                    disabledDate={disabledDate}
                    className={styles.calendarPicker}
                    fullscreen={false}
                />
            </div>
        </div>
    )

    return (
        <Popover
            content={content}
            trigger={isDisabled ? [] : 'click'}
            open={isDisabled ? false : open}
            onOpenChange={handleOpenChange}
            placement={placement}
            autoAdjustOverflow
            align={{
                overflow: {
                    adjustX: true,
                    adjustY: true,
                },
            }}
            overlayClassName={styles.datePickerPopover}
            overlayStyle={{ maxWidth: 'calc(100vw - 24px)' }}
            getPopupContainer={getPopupContainer || (() => document.body)}
            destroyTooltipOnHide
        >
            <div
                ref={triggerRef}
                className={styles.datePickerTriggerWrap}
                style={isDisabled ? { cursor: 'default' } : undefined}
            >
                {dateRangeText ? (
                    <div
                        className={`${styles.dateRangeChip} ${
                            isDetailDrawerPicker
                                ? styles.dateRangeChipHover
                                : ''
                        }`}
                        style={{
                            padding:
                                parentComponent === 'taskListView'
                                    ? '6px 0px'
                                    : '6px 12px',
                        }}
                    >
                        {isDetailDrawerPicker && (
                            <Icon
                                icon="akar-icons:calendar"
                                width={16}
                                height={16}
                                className={styles.calendarIcon}
                            />
                        )}
                        <span
                            className={styles.dateRangeText}
                            style={{
                                fontSize: isDetailDrawerPicker ? 14 : 12,
                            }}
                        >
                            {dateRangeText}
                        </span>
                        {isDetailDrawerPicker && showClear && (
                            <Icon
                                icon="mdi:close"
                                width={14}
                                height={14}
                                className={styles.closeIcon}
                                onClick={handleClear}
                            />
                        )}
                    </div>
                ) : (
                    <div className={styles.datePickerTrigger}>
                        <div
                            className={styles.iconWrapper}
                            style={{
                                width: isDetailDrawerPicker ? '28px' : '24px',
                                height: isDetailDrawerPicker ? '28px' : '24px',
                            }}
                        >
                            <Icon
                                icon="akar-icons:calendar"
                                width={isDetailDrawerPicker ? 16 : 14}
                                height={isDetailDrawerPicker ? 16 : 14}
                                style={{ color: '#94a3b8' }}
                            />
                        </div>
                        <span className={styles.placeholderText}>
                            {placeholder}
                        </span>
                    </div>
                )}
            </div>
        </Popover>
    )
}

export default DateRangePicker
